import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import { Formik, Form } from 'formik';
import { Heading, Box, SimpleGrid, Button, Flex, useDisclosure, Text } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { createItem, getItems, editItem, deleteItem } from 'services/items';
import { validateLongLat } from 'services/portfolios';
import { getAvailableItemTypes } from '../../services/itemCreationWizard';
import { ReactComponent as AddIcon } from '../../icons/add.svg';
import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';

import mappedItemTypes from 'services/items/mappedItemTypes';
import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';
import SecondaryButton from 'components/utils/SecondaryButton';
import AutomaticFormikDirtyCheck from '../forms/AutomaticFormikDirtyCheck';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import ConfirmationModal from '../modal/ConfirmationModal';
import useItemCreationWizard from 'hooks/useItemCreationWizard';
import AddItemButton from '../../components/utils/AddItemButton';

const ItemFormStep = () => {
    const itemFormRef = useRef();
    const intl = useIntl();
    const deleteItemModal = useDisclosure();
    const cancelModal = useDisclosure();

    const [isLoading, setIsLoading] = useState(true);
    const [showDescription, setShowDescription] = useState(false);
    const [showPricingPoints, setShowPricingPoints] = useState(false);
    const [showGasBasis, setShowGasBasis] = useState(false);
    const [showCO2Basis, setShowCO2Basis] = useState(false);
    const [nextStepIsLoading, setNextStepIsLoading] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [availableItemTypes, setAvailableItemTypes] = useState([]);
    const [itemNameError, setItemNameError] = useState('');
    const [isPricingPointsRefreshing, setIsPricingPointsRefreshing] = useState(false);

    const dataForItemCreation = useSelector((state) => state.item.dataForItemCreation);

    const {
        bookId: selectedBookId,
        itemTypeId: selectedItemTypeId,
        itemTypeName,
        newItemId,
        formDataForItemCreation: itemFormData,
        updateCreatedItemId,
        moveToItemPageStep,
        onFormHasUnsavedChanges,
        updateItemFormData,
        updateBookId,
        updateItemTypeId,
        updateItemTypeName,
        updateAutoForm,
        updatePricingPoints,
        moveToBasisSetupStep,
        moveToSubItemDecisionStep,
    } = useItemCreationWizard();

    useCallback(() => {
        onFormHasUnsavedChanges(isFormDirty);
    }, [isFormDirty, onFormHasUnsavedChanges]);

    const handleSubmit = async (formValues) => {
        //perform validations requiring asynchronous calls
        //determine if item name is unique for the selected book
        let valid = true;
        await getItems(selectedItemTypeId, formValues.description, false).then((data) => {
            if (
                Array.isArray(data) &&
                data.length > 0 &&
                data.find((item) => item.bookId === selectedBookId && item.id !== newItemId)
            ) {
                const message = intl.formatMessage({ id: 'common_unique_name_error' }, { label: 'Item' });
                setItemNameError(message);
                valid = false;
            }
        });

        if (valid) {
            const payload = {
                description: formValues.description,
                bookId: selectedBookId,
                itemTypeId: selectedItemTypeId,
                pricingPointId: formValues.pricingPointId || null,
                rtelecBasisPriceId: formValues.rtelecBasisPriceId || null,
                longitude: formValues.longitude !== '' ? formValues.longitude : null,
                latitude: formValues.latitude !== '' ? formValues.latitude : null,
                gasBasisId: formValues.gasBasisId || null,
                cO2BasisId: formValues.cO2BasisId || null,
            };

            const updateFormValues = {
                description: formValues.description,
                bookId: selectedBookId,
                itemTypeId: selectedItemTypeId,
                pricingPointId: formValues.pricingPointId || '',
                rtelecBasisPriceId: formValues.rtelecBasisPriceId || '',
                longitude: formValues.longitude !== '' ? formValues.longitude : '',
                latitude: formValues.latitude !== '' ? formValues.latitude : '',
                gasBasisId: formValues.gasBasisId || '',
                cO2BasisId: formValues.cO2BasisId || '',
            };

            updateItemFormData(updateFormValues);
            setNextStepIsLoading(true);

            try {
                if (newItemId !== null && newItemId > 0) {
                    await editItem(newItemId, payload).then(() => {
                        moveToSubItemDecisionStep();
                        onFormHasUnsavedChanges(false);
                    });
                } else {
                    await createItem(payload).then(async () => {
                        await getItems(payload.itemTypeId, payload.description, false).then((data) => {
                            updateCreatedItemId(data.find((item) => item.bookId === selectedBookId).id);
                            moveToSubItemDecisionStep();
                            onFormHasUnsavedChanges(false);
                        });
                    });
                }
            } catch (error) {
                setNextStepIsLoading(false);
            }
        }
    };

    useEffect(() => {
        // Create a BroadcastChannel
        const basisChannel = new BroadcastChannel('basis_channel');

        // Listen for message informing wizard to update the list of pricing points
        basisChannel.onmessage = (event) => {
            updatePricingPoints();
        };

        return () => {
            basisChannel.close();
        };
    }, [updatePricingPoints]);

    useEffect(() => {
        setNextStepIsLoading(false);
        setIsLoading(true);

        if (availableItemTypes.length === 0) {
            getAvailableItemTypes()
                .then((data) => {
                    setAvailableItemTypes(data);
                })
                .finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
    }, [availableItemTypes]);

    useEffect(() => {
        if (selectedBookId > 0 && selectedItemTypeId > 0) {
            setShowDescription(true);
            if (itemFormData?.description !== '') {
                if (!showPricingPoints) {
                    setShowPricingPoints(true);
                }
                if (selectedItemTypeId === mappedItemTypes.GENERATION_ASSET.id) {
                    setShowGasBasis(true);
                    setShowCO2Basis(true);
                }
            }
        }
    }, [itemFormData?.description, selectedItemTypeId, selectedBookId, showPricingPoints]);

    const handleDescriptionChange = (val) => {
        if (val?.target?.value !== null) {
            if (!showPricingPoints) {
                setShowPricingPoints(true);
            }
            if (selectedItemTypeId === mappedItemTypes.GENERATION_ASSET.id) {
                setShowGasBasis(true);
                setShowCO2Basis(true);
            }
        }
    };

    const handleBookChange = (val) => {
        if (val > 0) {
            updateBookId(val);
            if (selectedItemTypeId > 0) {
                setShowDescription(true);
            }
        } else {
            setShowDescription(false);
            updateItemTypeId(null);
        }
    };

    const handleItemTypeChange = (val) => {
        if (val > 0) {
            updateItemTypeId(val);
            updateAutoForm(availableItemTypes.find((itemType) => itemType.itemTypeId === val)?.formName);
            updateItemTypeName(availableItemTypes.find((itemType) => itemType.itemTypeId === val)?.labelName);
            if (selectedBookId > 0) {
                setShowDescription(true);
            }
        } else {
            setShowDescription(false);
            updateItemTypeId(null);
        }
    };

    const validate = (values) => {
        let errors = {};

        //validate description
        if (showDescription) {
            if (showPricingPoints) {
                const description = validateDescription(values?.description);
                if (description) errors.description = description;
            } else if (values?.description) {
                handleDescriptionChange(values?.description);
            }
        }

        //validate latitude and longitude
        let longitude = validateLongLat(values?.longitude, 'long', values?.latitude);
        let latitude = validateLongLat(values?.latitude, 'lat', values?.longitude);
        if (longitude) errors.longitude = longitude;
        if (latitude) errors.latitude = latitude;

        return errors;
    };

    const validateDescription = (value) => {
        setItemNameError('');

        const trimmed = value?.trim() ?? '';
        if (trimmed.length > 100) {
            setItemNameError(
                intl.formatMessage(
                    { id: 'common_forms_validation_length' },
                    { label: 'Item name', lengthRule: '100 characters or less' }
                )
            );
        } else if (trimmed === '') {
            const message = intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Item Name' });
            setItemNameError(message);
            return message;
        }
        handleDescriptionChange(trimmed);
    };

    const onItemDelete = async () => {
        await deleteItem(newItemId);
        updateItemTypeId(null);
        onFormHasUnsavedChanges(false);
        moveToItemPageStep();
    };

    const onCancel = () => {
        updateItemTypeId(-1);
        onFormHasUnsavedChanges(false);
        moveToItemPageStep();
    };

    const formLabels = useMemo(
        () => ({
            book: intl.formatMessage({ id: 'common_book' }),
            pricingPoint: intl.formatMessage({ id: 'common_electric_pricing_point' }),
            rtelecBasis: intl.formatMessage({ id: 'common_rtelec_pricing_point' }),
            selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
            unitType: intl.formatMessage({ id: 'unit_type' }),
            dsmLoad: intl.formatMessage({ id: 'common_dsm_load' }),
            latitude: intl.formatMessage({ id: 'latitude' }),
            longitude: intl.formatMessage({ id: 'longitude' }),
            master: intl.formatMessage({ id: 'common_master' }),
            gasBasis: intl.formatMessage({ id: 'common_gas_pricing_point' }),
            co2Basis: intl.formatMessage({ id: 'common_CO2_pricing_point' }),
            itemType: intl.formatMessage({ id: 'realized_values_item_type' }),
            description: intl.formatMessage({ id: 'common_item_name' }),
        }),
        [intl]
    );

    const onCreateNewBasis = useCallback(
        (formValues) => {
            const updateFormValues = {
                description: formValues.description,
                bookId: selectedBookId,
                itemTypeId: selectedItemTypeId,
                pricingPointId: formValues.pricingPointId || '',
                rtelecBasisPriceId: formValues.rtelecBasisPriceId || '',
                longitude: formValues.longitude !== '' ? formValues.longitude : '',
                latitude: formValues.latitude !== '' ? formValues.latitude : '',
                gasBasisId: formValues.gasBasisId || '',
                cO2BasisId: formValues.cO2BasisId || '',
            };

            updateItemFormData(updateFormValues);
            moveToBasisSetupStep();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [moveToBasisSetupStep, updateItemFormData]
    );

    const refreshPricingPoints = useCallback(() => {
        setIsPricingPointsRefreshing(true);
        updatePricingPoints();
    }, [updatePricingPoints]);

    useEffect(() => {
        // update IsLoading for the Refresh Button when the pricing points are refreshed
        if (isPricingPointsRefreshing) {
            setIsPricingPointsRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForItemCreation?.pricingPoints]);

    return (
        <>
            {isLoading ? (
                <CenteredWrapper>
                    <Heading pt={7} pb={0}>
                        <FormattedMessage id="item_creation_wizard_step1_heading" />
                    </Heading>
                    <DefinitionsSkeleton />
                </CenteredWrapper>
            ) : (
                <Formik
                    initialValues={itemFormData}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                    validate={validate}
                    innerRef={itemFormRef}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ isSubmitting, isValidating, isValid, errors, touched, values }) => {
                        return (
                            <Form>
                                <CenteredWrapper>
                                    <Heading pt={7} pb={0}>
                                        <FormattedMessage id="item_creation_wizard_step1_heading" />
                                    </Heading>
                                    <AutomaticFormikDirtyCheck
                                        isFormDirty={isFormDirty}
                                        onFormDirtyChange={setIsFormDirty}
                                    />

                                    <SimpleGrid pt={7} pb={0} columns={2} spacingX={6} spacingY={4}>
                                        <ComboboxFormikField
                                            id="bookId"
                                            name="bookId"
                                            label={formLabels.book}
                                            options={dataForItemCreation?.books}
                                            valueKey="id"
                                            labelKey="description"
                                            isFastField
                                            isRequired
                                            onChange={(value) => handleBookChange(value)}
                                            value={itemFormData?.bookId}
                                        />
                                        <ComboboxFormikField
                                            id="itemTypeId"
                                            name="itemTypeId"
                                            label={formLabels.itemType}
                                            options={availableItemTypes}
                                            valueKey="itemTypeId"
                                            labelKey="labelName"
                                            isFastField
                                            isRequired
                                            onChange={(value) => handleItemTypeChange(value)}
                                            value={itemFormData?.itemTypeId}
                                            isDisabled={newItemId !== null && newItemId > 0}
                                        />
                                    </SimpleGrid>

                                    {showDescription && (
                                        <SimpleGrid pt={7} pb={0} columns={1} spacingX={6} spacingY={4}>
                                            <InputFormikField
                                                name="description"
                                                type="text"
                                                label={formLabels.description}
                                                isRequired
                                                isDisabled={!showDescription}
                                                isInvalid={itemNameError}
                                                error={itemNameError}
                                                value={itemFormData?.description}
                                                isFastField
                                            />
                                        </SimpleGrid>
                                    )}

                                    {showDescription && showPricingPoints && (
                                        <SimpleGrid pt={7} pb={5} columns={2} spacingX={6} spacingY={4}>
                                            <InputFormikField
                                                id="latitude"
                                                name="latitude"
                                                label={formLabels.latitude}
                                                type="number"
                                                step="any"
                                                isFastField
                                                isDisabled={!showPricingPoints}
                                                value={itemFormData?.latitude}
                                            />
                                            <InputFormikField
                                                id="longitude"
                                                name="longitude"
                                                label={formLabels.longitude}
                                                type="number"
                                                step="any"
                                                isFastField
                                                isDisabled={!showPricingPoints}
                                                value={itemFormData?.longitude}
                                            />
                                        </SimpleGrid>
                                    )}

                                    {showDescription && showPricingPoints && (
                                        <>
                                            <Flex justify="space-between" align="center">
                                                <Heading
                                                    variant="h3"
                                                    textTransform="capitalize"
                                                    id="pricing-points"
                                                    my={5}
                                                >
                                                    <FormattedMessage id="common_pricing_points_heading" />
                                                    <Flex justifyContent="flex-start">
                                                        <AddItemButton
                                                            pl="0px !important"
                                                            m="0px !important"
                                                            leftIcon={<AddIcon />}
                                                            onClick={() => onCreateNewBasis(values)}
                                                        >
                                                            <Text size="sm" color="gray.900" pl={1} pt={1}>
                                                                <FormattedMessage id="common_create_pricing_point" />
                                                            </Text>
                                                        </AddItemButton>
                                                        <SecondaryButton
                                                            onClick={refreshPricingPoints}
                                                            size="sm"
                                                            variant="secondary"
                                                            leftIcon={<RefreshIcon />}
                                                            isLoading={isPricingPointsRefreshing}
                                                            loadingText={intl.formatMessage({ id: 'common_loading' })}
                                                            textTransform="capitalize"
                                                        >
                                                            <FormattedMessage id="common_refresh" />
                                                        </SecondaryButton>
                                                    </Flex>
                                                </Heading>
                                            </Flex>
                                            <SimpleGrid
                                                pt={0}
                                                pb={0}
                                                columns={1}
                                                spacingX={6}
                                                spacingY={4}
                                                w={{ base: '50%' }}
                                            >
                                                <ComboboxFormikField
                                                    id="pricingPointId"
                                                    name="pricingPointId"
                                                    label={formLabels.pricingPoint}
                                                    options={dataForItemCreation?.pricingPoints}
                                                    valueKey="id"
                                                    labelKey="description"
                                                    showPlaceholder={true}
                                                    placeholderValue={null}
                                                    isFastField
                                                    isDisabled={!showPricingPoints}
                                                    value={itemFormData?.pricingPointId}
                                                />
                                                <ComboboxFormikField
                                                    id="rtelecBasisPriceId"
                                                    name="rtelecBasisPriceId"
                                                    label={formLabels.rtelecBasis}
                                                    options={dataForItemCreation?.rtelecBasis}
                                                    valueKey="id"
                                                    labelKey="description"
                                                    showPlaceholder={true}
                                                    placeholderValue={null}
                                                    isFastField
                                                    isDisabled={!showPricingPoints}
                                                    value={itemFormData?.rtelecBasisPriceId}
                                                />
                                            </SimpleGrid>
                                        </>
                                    )}

                                    {showDescription && showGasBasis && showCO2Basis && (
                                        <SimpleGrid pt={4} pb={5} columns={{ xl: 2 }} spacingX={6} spacingY={4}>
                                            <ComboboxFormikField
                                                id="gasBasisId"
                                                name="gasBasisId"
                                                label={intl.formatMessage({ id: 'common_gas_pricing_point' })}
                                                options={dataForItemCreation?.gasBasis}
                                                labelKey="description"
                                                valueKey="id"
                                                showPlaceholder={true}
                                                placeholderValue={null}
                                                isFastField
                                                isDisabled={!showGasBasis}
                                                value={itemFormData?.gasBasisId}
                                            />
                                            <ComboboxFormikField
                                                id="cO2BasisId"
                                                name="cO2BasisId"
                                                label={intl.formatMessage({ id: 'common_CO2_pricing_point' })}
                                                options={dataForItemCreation?.cO2Basis}
                                                labelKey="description"
                                                valueKey="id"
                                                showPlaceholder={true}
                                                placeholderValue={null}
                                                isFastField
                                                isDisabled={!showCO2Basis}
                                                value={itemFormData?.cO2BasisId}
                                            />
                                        </SimpleGrid>
                                    )}
                                </CenteredWrapper>
                                <StickyButtonsWrapper>
                                    <Flex justifyContent="flex-end">
                                        <Button
                                            variant="secondary"
                                            textTransform="capitalize"
                                            onClick={
                                                newItemId !== null && newItemId > 0
                                                    ? deleteItemModal.onOpen
                                                    : cancelModal.onOpen
                                            }
                                            ml={5}
                                            isDisabled={isSubmitting || isValidating || nextStepIsLoading}
                                        >
                                            {newItemId !== null && newItemId > 0 ? (
                                                <FormattedMessage id="common_delete_item_btn" />
                                            ) : (
                                                <FormattedMessage id="common_cancel" />
                                            )}
                                        </Button>
                                        <Button
                                            type="submit"
                                            textTransform="capitalize"
                                            ml={5}
                                            isDisabled={
                                                itemNameError || !(showDescription && showPricingPoints) || !isValid
                                            }
                                            isLoading={isSubmitting || isValidating || nextStepIsLoading}
                                        >
                                            <FormattedMessage id="common_next_step" />
                                        </Button>
                                    </Flex>
                                </StickyButtonsWrapper>
                            </Form>
                        );
                    }}
                </Formik>
            )}

            {cancelModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={cancelModal.onClose}
                    header={<FormattedMessage id="item_creation_wizard_cancel_heading" />}
                    hasExtraStep
                    content={<FormattedMessage id="discard_changes_modal_text" />}
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_continue" />
                        </Box>
                    }
                    onConfirm={() => {
                        cancelModal.onClose();
                        onCancel();
                    }}
                />
            )}

            {deleteItemModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={deleteItemModal.onClose}
                    header={<FormattedMessage id="common_delete_modal_heading" values={{ item: itemTypeName }} />}
                    hasExtraStep
                    content={
                        <FormattedMessage
                            id="common_delete_modal_msg"
                            values={{
                                item: itemFormData.description,
                            }}
                        />
                    }
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_delete" />
                        </Box>
                    }
                    onConfirm={() => {
                        deleteItemModal.onClose();
                        onItemDelete();
                    }}
                />
            )}
        </>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

const StickyButtonsWrapper = styled(Box)`
    width: 100%;
    position: fixed;
    bottom: 40px;
    min-height: 60px;
    background: var(--chakra-colors-background-tertiary);
    display: flex;
    align-items: center;
    padding: 10px 0px;
    border-top: 1px solid var(--chakra-colors-border-secondary);
    justify-content: flex-end;
    padding-right: 24px;
`;

export default ItemFormStep;
